import React, { useCallback, useEffect, useMemo } from 'react';
import { Checkbox, Divider, Flex, Switch, Tag, Text, Tooltip, FlexProps } from '@televet/kibble-ui';
import { GetSidePanelPetParentProfileQuery, SurveyType } from 'shared/types/graphql';
import { getClinicPetParentDisplayName } from 'shared/utils';
import useFeatureFlag from 'shared/hooks/useFeatureFlag';
import { FeatureFlagName } from 'shared/enums/FeatureFlagName';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import {
  MergeProfilesContactPreferences,
  MergeProfilesPhoneNumber,
  updateMergeProfilesResult,
} from 'shared/components/MergeProfiles/state/mergeProfilesSlice';
import MergeProfilesNoneTag from './MergeProfilesNoneTag';
import MergeProfilesLockIcon from './MergeProfilesLockIcon';
import MergeProfilesPhoneNumberListItem from './MergeProfilesPhoneNumberListItem';
import { isNumberVerifiedMobile } from '../utils';
import { useIntegrationsProvider } from 'shared/providers/IntegrationsProvider';
import { useTracking } from 'shared/providers/TrackingProvider';
import { MergeProfileEvents } from 'shared/providers/TrackingProvider/EventNames';

enum ContactMethod {
  SMS = 'sms',
  Email = 'email',
}

interface IMergeProfilesProfileCardProps extends FlexProps {
  cardTitle: string;
  clientProfile?: GetSidePanelPetParentProfileQuery['clinicPetParent'];
  isPrimaryProfile?: boolean;
  isMergeResult?: boolean;
}

const MergeProfilesProfileCard = ({
  cardTitle,
  clientProfile,
  isPrimaryProfile,
  isMergeResult,
  ...rest
}: IMergeProfilesProfileCardProps): JSX.Element => {
  const { isFeatureEnabled } = useFeatureFlag();
  const { primaryIntegrationName } = useIntegrationsProvider();
  const { trackEvent } = useTracking();

  const dispatch = useAppDispatch();
  const mergeProfilesResult = useAppSelector((state) => state.mergeProfiles.mergeProfileResult);
  const pimsProfile = useAppSelector((state) => state.mergeProfiles.pimsProfile);

  const surveyType = isFeatureEnabled(FeatureFlagName.NpsSurvey) ? SurveyType.Nps : SurveyType.Csat;

  const borderColor = useMemo(() => (isMergeResult ? 'border.strong' : 'border.default'), [isMergeResult]);

  const clientName = isMergeResult
    ? mergeProfilesResult?.name
    : getClinicPetParentDisplayName({
        firstName: clientProfile?.firstName,
        lastName: clientProfile?.lastName,
      });

  const email = useMemo(
    () => (isMergeResult ? mergeProfilesResult?.email : clientProfile?.email),
    [clientProfile?.email, isMergeResult, mergeProfilesResult?.email],
  );

  const phoneNumbers: MergeProfilesPhoneNumber[] | undefined = useMemo(
    () =>
      isMergeResult
        ? mergeProfilesResult?.phoneNumbers.filter((number) => number.isSelected)
        : clientProfile?.phoneNumbers
            ?.filter((number) => !number.isDeleted)
            .map((number): MergeProfilesPhoneNumber => {
              return {
                id: number.id,
                number: number.number,
                isPrimary: !!number.isPrimary,
                isSelected: !!isPrimaryProfile,
                verifiedMobile: isNumberVerifiedMobile(number.phoneNumberLookupResult),
              };
            }),
    [clientProfile?.phoneNumbers, isMergeResult, isPrimaryProfile, mergeProfilesResult?.phoneNumbers],
  );

  const hasVerifiedMobileNumbers = useMemo(() => {
    return !!phoneNumbers?.some((number) => number.verifiedMobile);
  }, [phoneNumbers]);

  const pets = useMemo(
    () =>
      isMergeResult
        ? mergeProfilesResult?.pets
        : clientProfile?.pets?.map((pet) => {
            return {
              id: pet.id,
              name: pet.name,
            };
          }),
    [clientProfile?.pets, isMergeResult, mergeProfilesResult?.pets],
  );

  const noContactMethodsEnabled = useMemo(() => {
    if (!clientProfile?.petParentSetting) return true;

    const { emailNotifications, emailOptedOutByPetParent, smsNotifications, smsOptedOutByPetParent } =
      clientProfile?.petParentSetting;

    return (!emailNotifications || emailOptedOutByPetParent) && (!smsNotifications || smsOptedOutByPetParent);
  }, [clientProfile?.petParentSetting]);

  // set opt outs on inital card load
  useEffect(() => {
    if (isPrimaryProfile && !!clientProfile?.petParentSetting && !!mergeProfilesResult?.contactPreferences) {
      const { emailOptedOut, smsOptedOut } = mergeProfilesResult.contactPreferences;
      const { emailOptedOutByPetParent, smsOptedOutByPetParent } = clientProfile.petParentSetting;

      if (emailOptedOut === undefined && smsOptedOut === undefined) {
        dispatch(
          updateMergeProfilesResult({
            contactPreferences: {
              ...mergeProfilesResult.contactPreferences,
              emailOptedOut: emailOptedOutByPetParent,
              smsOptedOut: smsOptedOutByPetParent,
            },
          }),
        );
      }
    }
  }, [clientProfile?.petParentSetting, dispatch, isPrimaryProfile, mergeProfilesResult?.contactPreferences]);

  const handleSendSurveyToggle = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const sendSurvey = !!e?.currentTarget?.checked;

      trackEvent(MergeProfileEvents.EditSendSurveyToggle);

      dispatch(
        updateMergeProfilesResult({
          ...mergeProfilesResult,
          sendSurvey,
        }),
      );
    },
    [dispatch, mergeProfilesResult, trackEvent],
  );

  const handleContactPreferenceSelected = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, method: ContactMethod) => {
      const selected = !!e.currentTarget.checked;

      trackEvent(MergeProfileEvents.SetContactMethod, { method, selected });

      const currentPrefs = mergeProfilesResult?.contactPreferences;

      const contactPreferences: MergeProfilesContactPreferences = {
        email: !!currentPrefs?.email,
        sms: !!currentPrefs?.sms,
        emailOptedOut: !!currentPrefs?.emailOptedOut,
        smsOptedOut: !!currentPrefs?.smsOptedOut,
        [method]: selected,
      };

      dispatch(
        updateMergeProfilesResult({
          ...mergeProfilesResult,
          contactPreferences: {
            ...contactPreferences,
          },
        }),
      );
    },
    [trackEvent, mergeProfilesResult, dispatch],
  );

  const handleProfileNameSelected = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      const selected = !!e.currentTarget.checked;

      if (selected) {
        dispatch(updateMergeProfilesResult({ name: clientName }));
      }
    },
    [clientName, dispatch],
  );

  const handleEmailSelected = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      const selected = !!e.currentTarget.checked;

      if (selected) {
        dispatch(updateMergeProfilesResult({ email: clientProfile?.email || '' }));
      }
    },
    [clientProfile?.email, dispatch],
  );

  return (
    <Flex
      height="fit-content"
      direction="column"
      w="260px"
      background={isMergeResult ? 'background.primaryAlpha' : 'background.default'}
      border="1px"
      borderColor={borderColor}
      borderRadius={12}
      py={2}
      {...rest}
    >
      <Flex mx={3} mb={3} alignItems="center">
        <Text fontWeight="bold" size="xs">
          {cardTitle}
        </Text>
        {!!clientProfile?.pimsId && !isMergeResult && (
          <Tag label={`In ${primaryIntegrationName}`} ml={2} backgroundColor="background.info" size="sm" />
        )}
      </Flex>
      <Divider borderColor={borderColor} />
      <Flex direction="column" mx={3} my={3}>
        <Text size="xs" variant="subtle">
          Profile Name
        </Text>
        <Tooltip
          label={
            pimsProfile
              ? `This option is locked because one of the selected profiles is already in ${primaryIntegrationName}`
              : ''
          }
          titleCaseLabel={false}
        >
          <Flex mx={1} pt={2} gap={2} alignItems="center">
            {!isMergeResult && (
              <Checkbox
                isChecked={mergeProfilesResult.name === clientName}
                isDisabled={pimsProfile}
                onChange={handleProfileNameSelected}
                isFieldInline
                pt={1}
              />
            )}
            <Text size="sm" overflow="auto">
              {isMergeResult ? mergeProfilesResult?.name : clientName}
            </Text>
            {!isMergeResult && pimsProfile && <MergeProfilesLockIcon />}
          </Flex>
        </Tooltip>
      </Flex>
      <Divider borderColor={borderColor} />
      <Flex direction="column" mx={3} my={3}>
        <Text size="xs" variant="subtle">{`Phone Number${phoneNumbers && phoneNumbers.length > 1 ? 's' : ''}`}</Text>
        {phoneNumbers?.length ? (
          <Flex justifyContent="flex-start" direction="column" mx={1} pt={2} gap={2} alignItems="center">
            {phoneNumbers.map((number) => {
              return (
                <MergeProfilesPhoneNumberListItem
                  key={number.id}
                  number={number}
                  isMergeResult={!!isMergeResult}
                  isPrimaryProfile={!!isPrimaryProfile}
                  disabled={!!clientProfile?.pimsId}
                  pimsName={primaryIntegrationName}
                  primaryDisabled={hasVerifiedMobileNumbers && !number.verifiedMobile}
                />
              );
            })}
          </Flex>
        ) : (
          <MergeProfilesNoneTag
            includeCheckbox={!isMergeResult}
            checkboxProps={{ isDisabled: true, isChecked: false }}
          />
        )}
      </Flex>
      <Divider borderColor={borderColor} />
      <Flex direction="column" mx={3} my={3}>
        <Text size="xs" variant="subtle">
          Email
        </Text>
        <Tooltip
          label={
            pimsProfile
              ? `This option is locked because one of the selected profiles is already in ${primaryIntegrationName}`
              : ''
          }
          titleCaseLabel={false}
        >
          <Flex mx={1} pt={2} gap={2} alignItems="center">
            {!isMergeResult && (
              <Checkbox
                isChecked={mergeProfilesResult.email === clientProfile?.email}
                isDisabled={pimsProfile}
                onChange={handleEmailSelected}
                isFieldInline
                pt={1}
              />
            )}
            <Text size="sm" overflow="auto">
              {email || '(None)'}
            </Text>
            {!isMergeResult && pimsProfile && <MergeProfilesLockIcon />}
          </Flex>
        </Tooltip>
      </Flex>
      <Divider borderColor={borderColor} />
      <Flex direction="column" mx={3} my={3}>
        <Text size="xs" variant="subtle">
          Send {surveyType} Survey
        </Text>
        <Flex alignItems="center" mx={2} mt={2} gap={2}>
          {isMergeResult ? (
            <>
              <Flex width="fit-content">
                <Switch isChecked={!!mergeProfilesResult?.sendSurvey} onChange={handleSendSurveyToggle} />
              </Flex>
              <Text size="sm">{!!mergeProfilesResult.sendSurvey ? 'ON' : 'OFF'}</Text>
            </>
          ) : (
            <Text size="sm">{clientProfile?.petParentSetting?.sendSurvey ? 'On' : 'Off'}</Text>
          )}
        </Flex>
      </Flex>
      <Divider borderColor={borderColor} />
      <Flex direction="column" mx={3} my={3}>
        <Text size="xs" variant="subtle">
          Contact Preferences
        </Text>
        {(clientProfile?.petParentSetting?.smsNotifications || isMergeResult) && (
          <Tooltip
            label={
              mergeProfilesResult.contactPreferences?.smsOptedOut
                ? 'This option is disabled because the client opted out of SMS communication.'
                : ''
            }
            titleCaseLabel={false}
          >
            <Flex mx={1} pt={2} gap={2} alignItems="center">
              {isMergeResult && (
                <Checkbox
                  defaultChecked={true}
                  isChecked={
                    !!mergeProfilesResult.contactPreferences?.sms &&
                    !mergeProfilesResult.contactPreferences?.smsOptedOut
                  }
                  onChange={(e): void => handleContactPreferenceSelected(e, ContactMethod.SMS)}
                  isDisabled={!!mergeProfilesResult.contactPreferences?.smsOptedOut}
                  isFieldInline
                  pt={1}
                />
              )}
              <Text size="sm">Text</Text>
            </Flex>
          </Tooltip>
        )}
        {(clientProfile?.petParentSetting?.emailNotifications || isMergeResult) && (
          <Tooltip
            label={
              mergeProfilesResult.contactPreferences?.emailOptedOut
                ? 'This option is disabled because the client opted out of email communication.'
                : ''
            }
            titleCaseLabel={false}
          >
            <Flex mx={1} pt={2} gap={2} alignItems="center">
              {isMergeResult && (
                <Checkbox
                  defaultChecked={true}
                  isChecked={
                    !!mergeProfilesResult.contactPreferences?.email &&
                    !mergeProfilesResult.contactPreferences?.emailOptedOut
                  }
                  onChange={(e): void => handleContactPreferenceSelected(e, ContactMethod.Email)}
                  isDisabled={!!mergeProfilesResult.contactPreferences?.emailOptedOut}
                  isFieldInline
                  pt={1}
                />
              )}
              <Text size="sm">Email</Text>
            </Flex>
          </Tooltip>
        )}
        {noContactMethodsEnabled && !isMergeResult && <MergeProfilesNoneTag />}
      </Flex>
      <Divider borderColor={borderColor} />
      <Flex direction="column" mx={3} mt={3}>
        <Text size="xs" variant="subtle">{`Pet${!pets || pets.length > 1 ? 's' : ''}`}</Text>
        {pets?.map((pet) => {
          return (
            <Tooltip
              key={pet.id}
              label={`All pets are automatically added to the merged profile.`}
              titleCaseLabel={false}
            >
              <Flex mx={1} pt={2} gap={2} alignItems="center">
                {!isMergeResult && <Checkbox isFieldInline pt={1} isChecked={true} isDisabled />}
                <Text size="sm" overflow="auto">
                  {pet.name}
                </Text>
                {!isMergeResult && <MergeProfilesLockIcon />}
              </Flex>
            </Tooltip>
          );
        })}
      </Flex>
    </Flex>
  );
};

export default MergeProfilesProfileCard;
